import type {
  StorefrontCustomProductCollectionLocalizedText,
  StorefrontCustomProductCollectionRef,
  StorefrontHeroLocale,
} from '../../types/storefront-engine';

type UnknownRecord = Record<string, unknown>;

export const STOREFRONT_CUSTOM_PRODUCT_COLLECTION_REF_RE = /^pc_[a-z0-9]{16,64}$/;
export const STOREFRONT_CUSTOM_PRODUCT_COLLECTION_NAME_MAX_LENGTH = 80;
export const STOREFRONT_CUSTOM_PRODUCT_COLLECTION_DESCRIPTION_MAX_LENGTH = 240;
export const STOREFRONT_CUSTOM_PRODUCT_COLLECTION_PRODUCT_REF_LIMIT = 24;
export const STOREFRONT_CUSTOM_PRODUCT_COLLECTION_ROUTE_SEGMENT = 'custom';
export const STOREFRONT_CUSTOM_PRODUCT_COLLECTION_LOCALES = [
  'ar',
  'he',
  'en',
] as const satisfies readonly StorefrontHeroLocale[];

const LOCALIZED_KEYS = ['ar', 'he', 'en'] as const;
const UNSAFE_TEXT_RE =
  /<|>|<\/|https?:\/\/|www\.|javascript:|data:|vbscript:|on[a-z]+\s*=|style\s*=|iframe|script|<style|<\/style|[\u007f-\u009f\u202a-\u202e\u2066-\u2069]/iu;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: UnknownRecord, keys: readonly string[]): boolean {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}

function normalizePlainText(value: unknown, maxLength: number, required: boolean): string | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().replace(/\s+/gu, ' ');
  if (normalized.length > maxLength) return null;
  if (required && normalized.length === 0) return null;
  if (normalized.length > 0 && UNSAFE_TEXT_RE.test(normalized)) return null;
  for (const character of normalized) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint <= 0x1f || codePoint === 0x7f) return null;
  }
  return normalized;
}

export function isStorefrontCustomProductCollectionRef(
  value: unknown,
): value is StorefrontCustomProductCollectionRef {
  return typeof value === 'string' && STOREFRONT_CUSTOM_PRODUCT_COLLECTION_REF_RE.test(value);
}

export function sanitizeStorefrontCustomProductCollectionRef(
  value: unknown,
): StorefrontCustomProductCollectionRef | null {
  if (!isStorefrontCustomProductCollectionRef(value)) return null;
  return value;
}

export function buildStorefrontCustomProductCollectionPath(
  storeSlug: string,
  collectionRef: StorefrontCustomProductCollectionRef,
): string {
  return `/s/${encodeURIComponent(storeSlug)}/collections/${STOREFRONT_CUSTOM_PRODUCT_COLLECTION_ROUTE_SEGMENT}/${encodeURIComponent(collectionRef)}`;
}

export function sanitizeStorefrontCustomProductCollectionLocalizedText(
  value: unknown,
  options: {
    readonly required: boolean;
    readonly maxLength: number;
  },
): StorefrontCustomProductCollectionLocalizedText | null {
  if (!isRecord(value) || !hasExactKeys(value, LOCALIZED_KEYS)) return null;
  const ar = normalizePlainText(value.ar, options.maxLength, options.required);
  const he = normalizePlainText(value.he, options.maxLength, false);
  const en = normalizePlainText(value.en, options.maxLength, false);
  if (ar === null || he === null || en === null) return null;
  if (!options.required && ar.length === 0 && he.length === 0 && en.length === 0) {
    return { ar: '', he: '', en: '' };
  }
  return { ar, he, en };
}

export function sanitizeStorefrontCustomProductCollectionName(
  value: unknown,
): StorefrontCustomProductCollectionLocalizedText | null {
  return sanitizeStorefrontCustomProductCollectionLocalizedText(value, {
    required: true,
    maxLength: STOREFRONT_CUSTOM_PRODUCT_COLLECTION_NAME_MAX_LENGTH,
  });
}

export function sanitizeStorefrontCustomProductCollectionDescription(
  value: unknown,
): StorefrontCustomProductCollectionLocalizedText | null {
  if (value === null || value === undefined) return { ar: '', he: '', en: '' };
  return sanitizeStorefrontCustomProductCollectionLocalizedText(value, {
    required: false,
    maxLength: STOREFRONT_CUSTOM_PRODUCT_COLLECTION_DESCRIPTION_MAX_LENGTH,
  });
}

export function resolveStorefrontCustomProductCollectionText(
  value: StorefrontCustomProductCollectionLocalizedText | null | undefined,
  locale: StorefrontHeroLocale,
  fallbackLocale: StorefrontHeroLocale = 'ar',
): string {
  if (!value) return '';
  const preferred = value[locale]?.trim();
  if (preferred) return preferred;
  const fallback = value[fallbackLocale]?.trim();
  if (fallback) return fallback;
  return value.en?.trim() || value.he?.trim() || value.ar?.trim() || '';
}
